// Event labels (doc/feature/calendar-model.md): the labels a calendar keeps on its events, by name,
// and where a label's time went in a year (`calendar/label_stats`). Lengths are seconds; years,
// months and days are on the person's clock.

import { addDays, dayLabel, place, timeLabel } from './calendar.js'
import { routeHref } from './router.js'

const HOUR = 3600
const MONDAY = '2024-01-01'

/** A label's color, 1 to 8: FNV-1a 32-bit of its lowercased name, the same as the API's. */
export function labelColor(name) {
  let hash = 0x811c9dc5
  for (const byte of new TextEncoder().encode(String(name || '').toLowerCase())) {
    hash ^= byte
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return (hash % 8) + 1
}

/** A length: "25 min", "1 h 5 min", "3 h"; "0 min" under a minute. */
export function durationText(seconds) {
  const minutes = Math.floor(Math.max(seconds, 0) / 60)
  if (minutes < 60) return `${minutes} min`
  const rest = minutes % 60
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${minutes / 60} h`
}

/** A label's time in hours: "0 h", "45 min" under an hour, "2.5 h" under ten, then whole hours "128 h". */
export function labelHours(seconds) {
  const total = Math.max(seconds || 0, 0)
  if (!total) return '0 h'
  if (total < HOUR) return `${Math.max(1, Math.round(total / 60))} min`
  const hours = total / HOUR
  return hours < 10 ? `${Math.round(hours * 10) / 10} h` : `${Math.round(hours).toLocaleString('en-US')} h`
}

/** `year`'s time against the year before: `{ percent, value: "+12%", text: "+12% vs 2025" }`, or null when the year before had none. */
export function yearChange(total, lastYear, year) {
  if (!lastYear) return null
  const percent = Math.round(((total - lastYear) / lastYear) * 100)
  const value = `${percent > 0 ? '+' : ''}${percent}%`
  return { percent, value, text: `${value} vs ${year - 1}` }
}

/** The stat cards of a label's year (`calendar/label_stats`): Planned, change and All-day only when there is some. */
export function statCards(stats) {
  const current = stats.year === stats.this_year
  const change = yearChange(stats.spent + stats.planned, stats.last_year, stats.year)
  return [
    { key: 'spent', label: 'Spent', value: labelHours(stats.spent), hint: current ? 'so far this year' : `in ${stats.year}` },
    stats.planned && { key: 'planned', label: 'Planned', value: labelHours(stats.planned), hint: current ? 'for the rest of the year' : 'still ahead' },
    { key: 'events', label: 'Events', value: String(stats.events), hint: 'with a set time' },
    { key: 'week', label: 'Per week', value: labelHours(stats.per_week), hint: 'spent on average' },
    change && { key: 'change', label: `Against ${stats.year - 1}`, value: change.value, hint: `${labelHours(stats.last_year)} in ${stats.year - 1}`, tone: change.percent > 0 ? 'up' : change.percent < 0 ? 'down' : '' },
    stats.days && { key: 'days', label: 'All-day', value: `${stats.days} ${stats.days === 1 ? 'day' : 'days'}`, hint: 'not counted in hours' },
  ].filter(Boolean)
}

const monthDay = (month) => `2000-${String(month).padStart(2, '0')}-01`

/** Twelve month bars, Jan to Dec: spent and planned as shares (0 to 100) of the busiest month. */
export function monthRows(months) {
  const most = Math.max(1, ...(months || []).map((row) => row.spent + row.planned))
  return (months || []).map((row) => {
    const parts = [`${labelHours(row.spent)} spent`, row.planned && `${labelHours(row.planned)} planned`].filter(Boolean)
    return {
      month: row.month,
      label: dayLabel(monthDay(row.month), { month: 'short' }),
      spent: (row.spent / most) * 100,
      planned: (row.planned / most) * 100,
      title: `${dayLabel(monthDay(row.month), { month: 'long' })}: ${parts.join(', ')}`,
    }
  })
}

/** Seven weekday bars, Mon to Sun, with their share (0 to 100) of the busiest day. */
export function weekdayRows(weekdays) {
  const most = Math.max(1, ...(weekdays || []).map((row) => row.seconds))
  return (weekdays || []).map((row) => ({
    weekday: row.weekday,
    label: dayLabel(addDays(MONDAY, row.weekday - 1), { weekday: 'short' }),
    share: (row.seconds / most) * 100,
    hours: labelHours(row.seconds),
  }))
}

/** The top titles as table rows: hours and how many events. */
export function titleRows(titles) {
  return (titles || []).map((row, index) => ({
    key: index,
    title: row.title || '(no title)',
    hours: labelHours(row.seconds),
    count: row.count,
  }))
}

/**
 * A label's instances (`calendar/label_stats` `items`, latest first) by the month they start in, latest
 * month first: each with its day, time, title, length and the calendar link that opens its day
 * and the event.
 */
export function instanceGroups(items) {
  const months = new Map()
  for (const item of items || []) {
    const placed = place(item)
    const month = placed.firstDay.slice(0, 7)
    if (!months.has(month)) months.set(month, { key: month, label: dayLabel(`${month}-01`, { month: 'long', year: 'numeric' }), seconds: 0, rows: [] })
    const group = months.get(month)
    const days = Math.round((placed.endAt - placed.startAt) / 86400000)
    group.seconds += item.seconds || 0
    group.rows.push({
      key: `${item.event}:${item.instance ?? item.start}`,
      day: dayLabel(placed.firstDay, { weekday: 'short', month: 'short', day: 'numeric' }),
      time: placed.all_day ? 'All day' : `${timeLabel(placed.startAt)} - ${timeLabel(placed.endAt)}`,
      title: item.summary || '(no title)',
      length: placed.all_day ? `${days} ${days === 1 ? 'day' : 'days'}` : durationText(item.seconds),
      href: routeHref('calendar', { view: 'day', date: placed.firstDay, event: item.event, instance: item.instance ?? '', calendar: item.calendar }),
    })
  }
  return [...months.values()]
    .sort((a, b) => (a.key < b.key ? 1 : -1))
    .map(({ seconds, rows, ...group }) => ({ ...group, hours: labelHours(seconds), count: rows.length, rows }))
}

/** The calendar's label strip (`calendar/labels` items): each with its hours spent this year and its page. */
export function labelChips(items, year) {
  return (items || []).map((label) => ({
    name: label.name,
    color: label.color || labelColor(label.name),
    hours: labelHours(label.spent),
    title: `${label.name}: ${labelHours(label.spent)} spent in ${year}${label.planned ? `, ${labelHours(label.planned)} planned` : ''}`,
    href: routeHref('calendar-label', { name: label.name }),
  }))
}

/**
 * The labels to suggest for an event: the ones I use (`calendar/labels` items) it does not have,
 * matching `text` (case-insensitively, the ones that start with it first), at most `max`.
 */
export function labelSuggestions(mine, taken, text, max = 8) {
  const query = String(text || '').trim().toLowerCase()
  const used = new Set((taken || []).map((name) => name.toLowerCase()))
  const free = (mine || []).filter((label) => !used.has(label.name.toLowerCase()) && label.name.toLowerCase().includes(query))
  const starts = free.filter((label) => label.name.toLowerCase().startsWith(query))
  return [...starts, ...free.filter((label) => !starts.includes(label))].slice(0, max)
}

/** The label names after adding `name` (the spelling I already use, when I have it), or null when it is empty or already there. */
export function addLabelName(names, mine, name) {
  const text = String(name || '').trim()
  if (!text) return null
  const lower = text.toLowerCase()
  if ((names || []).some((entry) => entry.toLowerCase() === lower)) return null
  const known = (mine || []).find((label) => label.name.toLowerCase() === lower)
  return [...(names || []), known ? known.name : text]
}
